class SocialFloating extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing'
    };

    this.querySelectorAll('[data-social-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!theme.config.mqlSmall) return;

    this.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  open() {
    this.classList.add(this.classes.activeClass);

    // close promo-popup
    const popup = document.querySelector('promo-popup');
    popup && popup.close();
  }

  close() {
    this.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.classList.remove(this.classes.activeClass);
      this.classList.remove(this.classes.closingClass);
    }, 500);
  }
}
customElements.define('social-floating', SocialFloating);

class PopupFloatingContrast {
  constructor(el) {
    this.el = el;
    this.itemClass = 'popup-floating__item--on-light';
    this.itemSelector = '.list-social__link, .popup-open';
    this.rafId = null;
    this.onScroll = () => {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  getItems() {
    return Array.from(this.el.querySelectorAll(this.itemSelector)).filter((item) => {
      const style = getComputedStyle(item);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    });
  }

  update() {
    if (!this.el.isConnected) return;

    const items = this.getItems();
    if (!items.length) return;

    const isLeft = this.el.classList.contains('popup-floating--left');
    const previousVisibility = this.el.style.visibility;
    this.el.style.visibility = 'hidden';

    items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        item.classList.remove(this.itemClass);
        return;
      }

      const sampleX = isLeft
        ? Math.min(Math.round(rect.right + 8), window.innerWidth - 1)
        : Math.max(Math.round(rect.left - 8), 0);
      const sampleY = Math.round(rect.top + rect.height / 2);
      const stack = document.elementsFromPoint(sampleX, sampleY);
      const luminance = this.getLuminanceBehind(stack);
      item.classList.toggle(this.itemClass, luminance > 0.58);
    });

    this.el.style.visibility = previousVisibility;
  }

  getLuminanceBehind(stack) {
    for (const node of stack) {
      if (!(node instanceof Element)) continue;
      if (node.closest('.popup-floating, promo-popup, .shopify-section-header, .announcement-bar')) continue;

      if (
        node.matches(
          'video, canvas, iframe, img, picture, .media, .media-wrapper, [class*="video-"], [class*="banner__media"]'
        )
      ) {
        return 0.12;
      }

      const style = getComputedStyle(node);
      const bgImage = style.backgroundImage;
      if (bgImage && bgImage !== 'none') {
        return 0.2;
      }

      const parsed = this.parseColor(style.backgroundColor);
      if (!parsed || parsed.a < 0.2) continue;

      return this.relativeLuminance(parsed.r, parsed.g, parsed.b);
    }

    const bodyColor = this.parseColor(getComputedStyle(document.body).backgroundColor)
      || this.parseColor(getComputedStyle(document.documentElement).backgroundColor);
    if (!bodyColor) return 1;
    return this.relativeLuminance(bodyColor.r, bodyColor.g, bodyColor.b);
  }

  parseColor(value) {
    if (!value || value === 'transparent') return null;
    const match = value.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?/i);
    if (!match) return null;
    return {
      r: Number(match[1]),
      g: Number(match[2]),
      b: Number(match[3]),
      a: match[4] === undefined ? 1 : Number(match[4])
    };
  }

  relativeLuminance(r, g, b) {
    const channel = (value) => {
      const srgb = value / 255;
      return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  }
}

document.querySelectorAll('[data-floating-contrast]').forEach((el) => {
  new PopupFloatingContrast(el);
});

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('[data-floating-contrast]').forEach((el) => {
    new PopupFloatingContrast(el);
  });
});

class PromoPopup extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.cookieName = 'beyours:promo-popup';

    this.classes = {
      bodyClass: 'promo-popup--open',
      activeClass: 'is-active',
      closingClass: 'is-closing',
      showImage: 'show-image'
    };

    this.popup = this.querySelector('.promo-popup');

    this.querySelectorAll('[data-popup-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });

    // Open modal if errors or success message exist, and skip the delayed open
    const formMessage = this.querySelector('.form__message');
    if (formMessage) {
      this.open();

      // Don't show the popup again to someone who has just subscribed
      if (!formMessage.classList.contains('errors') && this.dataset.testMode !== 'true') {
        this.setCookie(this.cookieName, this.dataset.expiry);
      }
      return;
    }

    if (!this.getCookie(this.cookieName) || this.dataset.testMode === 'true') {
      this.init();
    }
  }

  connectedCallback() {
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);

      document.body.classList.remove(this.classes.bodyClass);
    }
  }
  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  init() {
    if(!this.popup) {
      return;
    }
    
    if (Shopify && Shopify.designMode) {
      return;
    }

    setTimeout(function() {
      this.open();
    }.bind(this), parseInt(this.dataset.delay) * 1000);
  }

  onButtonClick(event) {
    event.preventDefault();
    this.popup.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  open() {
    document.body.classList.remove(this.classes.bodyClass);
    this.popup.classList.add(this.classes.activeClass);
    setTimeout(() => {
      this.popup.classList.add(this.classes.showImage);
    }, 600);

    if (this.popup.dataset.position === 'center') {
      setScrollbarWidth();
      document.body.classList.add(this.classes.bodyClass);
    }

    // close social-floating
    const social = document.querySelector('social-floating');
    social && social.close();
  }

  close() {
    this.popup.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.popup.classList.remove(this.classes.activeClass);
      this.popup.classList.remove(this.classes.closingClass);
      this.popup.classList.remove(this.classes.showImage);

      if (this.popup.dataset.position === 'center') {
        document.body.classList.remove(this.classes.bodyClass);
      }
    }, 500);

    // Remove a cookie in case it was set in test mode
    if (this.dataset.testMode === 'true') {
      this.removeCookie(this.cookieName);
      return;
    }

    this.setCookie(this.cookieName, this.dataset.expiry);
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0`;
  }
}
customElements.define('promo-popup', PromoPopup);
